import { isZeroDecimalCurrency } from "../constants/money.js";
import { REGIONAL_DICTIONARY } from "../constants/regions.js";

const DEFAULT_CURRENCY = "EUR";

export const getCurrencyFractionDigits = (currency?: string) =>
  isZeroDecimalCurrency((currency ?? DEFAULT_CURRENCY).toUpperCase()) ? 0 : 2;

const getCurrencyDivisor = (currency?: string) =>
  10 ** getCurrencyFractionDigits(currency);

/** Converts an amount stored in the smallest currency unit to a display amount. */
export const toMajorUnits = (amount: number, currency?: string) =>
  amount / getCurrencyDivisor(currency);

/** Converts a form/display amount to the smallest currency unit used by the API. */
export const toMinorUnits = (amount: number | string, currency?: string) =>
  Math.round(Number(amount) * getCurrencyDivisor(currency));

/** Formats an amount stored in the smallest currency unit. */
export const formatMoney = (
  amount: number,
  currency = DEFAULT_CURRENCY,
  locale = "es",
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(toMajorUnits(amount, currency));

/** Formats a display/form amount, without applying a minor-unit conversion. */
export const formatMajorMoney = (
  amount: number,
  currency = DEFAULT_CURRENCY,
  locale = "es",
) =>
  new Intl.NumberFormat(locale, {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(amount);

export const money = {
  getFractionDigits: getCurrencyFractionDigits,
  toMajorUnits,
  toMinorUnits,
  format: formatMoney,
  formatMajor: formatMajorMoney,
  // Backwards-compatible aliases for existing EUR/cents call sites.
  toCents: (amount: number | string) => Math.round(Number(amount) * 100),
  toEuros: (cents: number) => (cents / 100).toFixed(2),
};

export function getLocalizedPrice(plan: any, countryCode: string) {
  const entry = REGIONAL_DICTIONARY[countryCode];
  if (!entry) return null;

  const currency = entry.platformBillingCurrency;
  const priceObj = plan.prices?.[currency];

  if (!priceObj || priceObj.amount == null) return null;

  const amount = priceObj.amount;

  return {
    currency,
    amount,
    formatted: formatMoney(amount, currency, entry.locale),
  };
}
